import { FC, useState } from 'react'
import Actions from '../../../actions'
import { Link } from 'react-router-dom'
import _ from 'lodash'
import { gaEvent } from '../../../common/services/analytics'
import { ProfileLink, Spinner } from '../../../components/generic'
import dayjs from 'dayjs'
import { Button, Icon } from '@lmc/ui-kit'
import { useDispatch } from 'react-redux'
import { Carer } from '@lmc/ts-common'
import { CardTitle, EmptyAvatars } from './CardComponents'
import styled from 'styled-components'

type Props = {
  isLoading?: boolean
  carers: Carer[]
}

const INIT_CARERS_DISPLAYED = 10
const TITLE = 'Team'
const ACTIVE_TODAY_PLURAL =
  'of your care team have been active recently (last 12 hrs)'
/* const ACTIVE_TODAY_SINGULAR =
  'of your care team has been active recently (last 12 hrs)' */
/* const NO_CARERS =
  "You haven't added any team members yet. You'll need to add them here before they can start using the Carer App or the Care Office." */
const NO_ACTIVE_CARERS = `It doesn’t look like any ${ACTIVE_TODAY_PLURAL}`
/* const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/the-care-office/get-started-in-minutes/how-do-i-add-a-new-carer-senior-or-administrator' */

export const CarersCard: FC<Props> = ({ isLoading, carers }) => {
  const [carerDisplayed, setCarerDisplayed] = useState(INIT_CARERS_DISPLAYED)

  const triggerGAEvent = ({
    action,
    label
  }: {
    action: string
    label?: string
  }) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label
    })

  const dispatch = useDispatch()

  const openModal = () => {
    const id = 'addTeamMember'
    dispatch(Actions.openModal({ modalId: 'addTeamMember' }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const showMore = () => {
    setCarerDisplayed((prev) => prev + 5)
    triggerGAEvent({ action: 'Click show_more_carers' })
  }

  const getActiveCarers = (carers: Carer[]) => {
    const cutoffTime = dayjs().subtract(12, 'h')
    return _.chain(carers)
      .filter((c) => {
        const ts = (c as any)?.lastLogTimestamp
        return ts ? dayjs(ts).isAfter(cutoffTime) : false
      })
      .sortBy('name')
      .value()
  }

  const carerCount = _.filter(carers, { active: true }).length

  const renderActiveCarers = (activeCarers: Carer[]) => {
    const hiddenCarers = activeCarers.length > carerDisplayed
    const nShowing = hiddenCarers ? carerDisplayed - 1 : carerDisplayed
    return (
      <div>
        {/* <p>
          <strong>
            {activeCarers.length}{' '}
            {activeCarers.length === 1 ? ' member ' : ' members '}
          </strong>
          {activeCarers.length === 1
            ? ACTIVE_TODAY_SINGULAR
            : ACTIVE_TODAY_PLURAL}
        </p> */}
        <div className="lmc-flex-grid">
          {_.take(activeCarers, nShowing).map((row) => (
            <ProfileLink
              onClick={() =>
                triggerGAEvent({ action: 'Click carer_profile_link' })
              }
              list="organisation/team"
              key={row.id}
              id={row.id}
              name={row.name}
              picture={row.picture}
            />
          ))}
          {hiddenCarers ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a className="lmc-more-link" onClick={showMore}>
              More...
            </a>
          ) : null}
        </div>
      </div>
    )
  }

  const renderNoCarers = () => <EmptyAvatars />

  const renderFooter = () => {
    if (isLoading) return null

    const link = `/organisation/list`

    const hasTeamMembers = carers?.length && carerCount > 2

    return (
      <Footer>
        {hasTeamMembers && (
          <ButtonContainer>
            <Link
              onClick={() =>
                triggerGAEvent({ action: 'Click view_all_carers' })
              }
              to={link}
            >
              <Button variant="default">View All</Button>
            </Link>
          </ButtonContainer>
        )}
        <div id="intro-js-step-add-carers">
          <Button
            variant="primary"
            onClick={() => {
              triggerGAEvent({ action: 'Click create_carer' })
              openModal()
            }}
            icon={<Icon name="MailOutlined" />}
            dataTestId="invite-a-team-member"
          >
            Invite a Team Member
          </Button>
        </div>
      </Footer>
    )
  }

  const renderBody = () => {
    const activeToday = getActiveCarers(carers)

    if (isLoading) return <StyledSpinner />

    if (!carers || carerCount < 2) return renderNoCarers()

    if (!activeToday || !activeToday.length)
      return <NoCarersMessage>{NO_ACTIVE_CARERS}</NoCarersMessage>

    return renderActiveCarers(activeToday)
  }

  const renderTitle = () => {
    const link = `/organisation/list`
    return (
      <StyledLink
        onClick={() => triggerGAEvent({ action: 'Click view_carers_title' })}
        to={link}
        data-cy="btn-team"
      >
        <CardTitle title={TITLE} />
      </StyledLink>
    )
  }

  return (
    <div>
      <div className="lmc-home-card">
        {renderTitle()}
        <div className="lmc-home-card-body-ss">{renderBody()}</div>
        <div className="lmc-home-card-footer">{renderFooter()}</div>
      </div>
    </div>
  )
}

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const ButtonContainer = styled.div`
  margin-right: 8px;
  max-width: 95px;
`

const StyledSpinner = styled(Spinner)`
  padding-top: 40px;
`

const NoCarersMessage = styled.p`
  margin-top: 0;
`

const StyledLink = styled(Link)`
  display: inline-block;
  text-decoration: none;
`

export default CarersCard
